import React, { useState, useEffect } from 'react';
import { Turnstile } from "@marsidev/react-turnstile";
import './LightningSubscription.css';

import fnnLogo from "../../client-dashboard/client-dashboard-assets/FNNLogoWithBlackFont-06.png"

// ─── Types ────────────────────────────────────────────────────────────────────
interface Location {
  location_id: number;
  location_name: string;
}

type View = 'menu' | 'subscribe' | 'update' | 'unsubscribe';
type Status = 'idle' | 'loading' | 'success' | 'error';

// ─── Constants ────────────────────────────────────────────────────────────────
const TURNSTILE_SITE_KEY = "0x4AAAAAACiHB4c31UPAbHHe";
const API_BASE = "api";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fetchCsrfToken = async (): Promise<string | null> => {
  const response = await fetch('/api/csrf-token/', {
    method: 'GET',
    credentials: "same-origin",
  });
  if (response.ok) {
    const data = await response.json();
    return data.csrfToken;
  } else {
    console.error('Failed to fetch CSRF token');
    return null;
  }
};

const normalizePhoneNumber = (input: string): string => {
  if (!input) return "";

  // remove everything except digits and +
  const value = input.trim();

  // If already E.164, return cleaned version
  if (value.startsWith("+")) {
    return "+" + value.slice(1).replace(/\D/g, "");
  }

  // otherwise strip everything non-digit
  const digits = value.replace(/\D/g, "");

  // US fallback (you can extend later for other countries)
  if (digits.length === 10) {
    return "+1" + digits;
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    return "+" + digits;
  }

  // fallback: return raw cleaned number (backend can reject if invalid)
  return "+" + digits;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StormIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 20C14 14.477 18.477 10 24 10C28.663 10 32.596 13.096 33.699 17.35C36.668 17.956 39 20.618 39 23.833C39 27.5 36.038 30.5 32.333 30.5H15.667C11.962 30.5 9 27.5 9 23.833C9 21.192 10.612 18.92 13 17.958"
      stroke="#28AAE1" strokeWidth="2" strokeLinecap="round" />
    <path d="M26 30.5L22 37.5H27L23 44.5"
      stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BackArrow = ({ onClick }: { onClick: () => void }) => (
  <button className="ls-back-btn" onClick={onClick}>
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    Back
  </button>
);

const LocationCheckbox = ({
  loc, checked, onChange
}: { loc: Location; checked: boolean; onChange: (id: number) => void }) => (
  <label className={`ls-checkbox-item ${checked ? 'ls-checked' : ''}`}>
    <div className={`ls-checkbox-box ${checked ? 'ls-checked' : ''}`}>
      {checked && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
    <input
      type="checkbox"
      className="ls-checkbox-hidden"
      checked={checked}
      onChange={() => onChange(loc.location_id)}
    />
    <span className={`ls-checkbox-text ${checked ? 'ls-checked' : ''}`}>
      {loc.location_name}
    </span>
  </label>
);

const StatusBanner = ({ status, message }: { status: 'success' | 'error'; message: string }) => (
  <div className={`ls-banner ${status}`}>{message}</div>
);

// ─── Menu View ────────────────────────────────────────────────────────────────

const MenuView = ({ onSelect }: { onSelect: (v: View) => void }) => {
  const options = [
    { view: 'subscribe' as View, icon: '⚡', label: 'Subscribe', desc: 'Sign up for lightning alerts at your locations' },
    { view: 'update' as View, icon: '📍', label: 'Update Locations', desc: 'Add more locations to your subscription' },
    { view: 'unsubscribe' as View, icon: '🚫', label: 'Unsubscribe', desc: 'Remove yourself from all alerts' },
  ];

  return (
    <div>
      <div className="ls-menu-header">
        <h1 className="ls-menu-title"> <StormIcon /> Lightning Alerts</h1>
        <p className="ls-menu-subtitle">Real-time storm notifications for your locations</p>
      </div>

      <div className="ls-menu-options">
        {options.map(({ view, icon, label, desc }) => (
          <button key={view} className="ls-menu-option-btn" onClick={() => onSelect(view)}>
            <span className="ls-menu-option-icon">{icon}</span>
            <div>
              <div className="ls-menu-option-label">{label}</div>
              <div className="ls-menu-option-desc">{desc}</div>
            </div>
            <svg className="ls-menu-option-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 4L10 8L6 12" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Subscribe View ───────────────────────────────────────────────────────────

const SubscribeView = ({ onBack, locations }: { onBack: () => void; locations: Location[] }) => {
  const [form, setForm] = useState({ name: '', email: '', phone_number: '' });
  const [selectedLocs, setSelectedLocs] = useState<number[]>([]);
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const toggleLoc = (id: number) =>
    setSelectedLocs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email && !form.phone_number) {
      setStatus('error'); setMessage('Please enter an email or phone number.'); return;
    }
    if (selectedLocs.length === 0) {
      setStatus('error'); setMessage('Please select at least one location.'); return;
    }
    if (turnstileToken) { submit(turnstileToken); return; }
    setShowTurnstile(true);
  };

  const submit = async (token: string) => {
    setStatus('loading');
    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setStatus('error'); setMessage('Could not get CSRF token.'); return; }

    try {
      const res = await fetch(`${API_BASE}/public/sms_subscribe/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({
          ...form,
          phone_number: normalizePhoneNumber(form.phone_number),
          locations: selectedLocs,
          turnstileToken: token,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Subscription failed');
      setStatus('success');
      setMessage("You're subscribed! You'll receive alerts when storms are detected near your locations.");
    } catch (err: any) {
      setStatus('error'); setMessage(err.message || 'Something went wrong.');
      setTurnstileToken(null); setShowTurnstile(false);
    }
  };

  if (status === 'success') return (
    <div>
      <BackArrow onClick={onBack} />
      <div className="ls-success-screen">
        <div className="ls-success-icon">✅</div>
        <h2 className="ls-success-title">Subscribed!</h2>
        <p className="ls-success-message">{message}</p>
      </div>
    </div>
  );

  return (
    <div>
      <BackArrow onClick={onBack} />
      <h2 className="ls-view-title">Subscribe to Alerts</h2>
      <p className="ls-view-subtitle">Get notified by SMS or email when lightning storms start and end near your locations.</p>

      <form onSubmit={handleSubmit}>
        <div className="ls-field">
          <label className="ls-label">Name *</label>
          <input className="ls-input" placeholder="Your name" value={form.name} required
            onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>

        <div className="ls-field">
          <label className="ls-label">Email</label>
          <input className="ls-input" type="email" placeholder="you@example.com" value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })} />
        </div>

        <div className="ls-field-last">
          <label className="ls-label">Phone Number</label>
          <input className="ls-input" type="tel" placeholder="+1 (555) 000-0000" value={form.phone_number}
            onChange={e => setForm({ ...form, phone_number: e.target.value })} />
          <p className="ls-field-hint">At least one of email or phone is required.</p>
        </div>

        <div className="ls-field-last">
          <label className="ls-label ls-locations-label">Locations *</label>

          <div className='ls-location-scroll'>
            {locations.length === 0
              ? <p className="ls-locations-loading">Loading locations...</p>


              : locations.map(loc => (
                <LocationCheckbox key={loc.location_id} loc={loc}
                  checked={selectedLocs.includes(loc.location_id)}
                  onChange={toggleLoc} />
              ))

            }
          </div>
        </div>

        {showTurnstile && (
          <div className="ls-turnstile">
            <Turnstile siteKey={TURNSTILE_SITE_KEY}
              options={{ theme: 'light', size: 'normal', language: 'auto' }}
              onSuccess={token => { setTurnstileToken(token); submit(token); }}
            />
          </div>
        )}

        {status === 'error' && <StatusBanner status="error" message={message} />}

        <button type="submit" className="ls-submit-btn" disabled={status === 'loading'}>
          {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
        </button>
      </form>
    </div>
  );
};

// ─── Update View ──────────────────────────────────────────────────────────────

const UpdateView = ({ onBack, locations }: { onBack: () => void; locations: Location[] }) => {
  const [identifier, setIdentifier] = useState({ email: '', phone_number: '' });
  const [selectedLocs, setSelectedLocs] = useState<number[]>([]);
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const toggleLoc = (id: number) =>
    setSelectedLocs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.email && !identifier.phone_number) {
      setStatus('error'); setMessage('Please enter your email or phone number.'); return;
    }
    if (selectedLocs.length === 0) {
      setStatus('error'); setMessage('Please select at least one location to add.'); return;
    }
    if (turnstileToken) { submit(turnstileToken); return; }
    setShowTurnstile(true);
  };

  const submit = async (token: string) => {
    setStatus('loading');
    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setStatus('error'); setMessage('Could not get CSRF token.'); return; }

    try {
      const res = await fetch(`${API_BASE}/public/sms_subscribers/update_locations/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({
          ...identifier,
          phone_number: normalizePhoneNumber(identifier.phone_number),
          locations: selectedLocs,
          turnstileToken: token,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      setStatus('success');
      setMessage('Your locations have been updated successfully.');
    } catch (err: any) {
      setStatus('error'); setMessage(err.message || 'Something went wrong.');
      setTurnstileToken(null); setShowTurnstile(false);
    }
  };

  if (status === 'success') return (
    <div>
      <BackArrow onClick={onBack} />
      <div className="ls-success-screen">
        <div className="ls-success-icon">📍</div>
        <h2 className="ls-success-title">Locations Updated!</h2>
        <p className="ls-success-message">{message}</p>
      </div>
    </div>
  );

  return (
    <div>
      <BackArrow onClick={onBack} />
      <h2 className="ls-view-title">Update Locations</h2>
      <p className="ls-view-subtitle">Enter your account details and select locations you would like coverage.</p>

      <form onSubmit={handleSubmit}>
        <div className="ls-field">
          <label className="ls-label">Email</label>
          <input className="ls-input" type="email" placeholder="you@example.com" value={identifier.email}
            onChange={e => setIdentifier({ ...identifier, email: e.target.value })} />
        </div>

        <div className="ls-field-last">
          <label className="ls-label">Phone Number</label>
          <input className="ls-input" type="tel" placeholder="+1 (555) 000-0000" value={identifier.phone_number}
            onChange={e => setIdentifier({ ...identifier, phone_number: e.target.value })} />
          <p className="ls-field-hint">Enter the email or phone used when you subscribed.</p>
        </div>

        <div className="ls-field-last">
          <label className="ls-label ls-locations-label">Update Locations *</label>

          <div className='ls-location-scroll'>
            {locations.map(loc => (
              <LocationCheckbox key={loc.location_id} loc={loc}
                checked={selectedLocs.includes(loc.location_id)}
                onChange={toggleLoc} />
            ))}

          </div>
        </div>

        {showTurnstile && (
          <div className="ls-turnstile">
            <Turnstile siteKey={TURNSTILE_SITE_KEY}
              options={{ theme: 'light', size: 'normal', language: 'auto' }}
              onSuccess={token => { setTurnstileToken(token); submit(token); }}
            />
          </div>
        )}

        {status === 'error' && <StatusBanner status="error" message={message} />}

        <button type="submit" className="ls-submit-btn" disabled={status === 'loading'}>
          {status === 'loading' ? 'Updating...' : 'Update Locations'}
        </button>
      </form>
    </div>
  );
};

// ─── Unsubscribe View ─────────────────────────────────────────────────────────

const UnsubscribeView = ({ onBack }: { onBack: () => void }) => {
  const [form, setForm] = useState({ email: '', phone_number: '' });
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email && !form.phone_number) {
      setStatus('error'); setMessage('Please enter your email or phone number.'); return;
    }
    if (turnstileToken) { submit(turnstileToken); return; }
    setShowTurnstile(true);
  };

  const submit = async (token: string) => {
    setStatus('loading');
    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setStatus('error'); setMessage('Could not get CSRF token.'); return; }

    try {
      const res = await fetch(`${API_BASE}/public/sms_unsubscribe/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({
          ...form,
          phone_number: normalizePhoneNumber(form.phone_number),
          turnstileToken: token,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unsubscribe failed');
      setStatus('success');
      setMessage('You have been successfully unsubscribed from all lightning alerts.');
    } catch (err: any) {
      setStatus('error'); setMessage(err.message || 'Something went wrong.');
      setTurnstileToken(null); setShowTurnstile(false);
    }
  };

  if (status === 'success') return (
    <div>
      <BackArrow onClick={onBack} />
      <div className="ls-success-screen">
        <div className="ls-success-icon">👋</div>
        <h2 className="ls-success-title">Unsubscribed</h2>
        <p className="ls-success-message">{message}</p>
      </div>
    </div>
  );

  return (
    <div>
      <BackArrow onClick={onBack} />
      <h2 className="ls-view-title">Unsubscribe</h2>
      <p className="ls-view-subtitle">Enter your email or phone number to remove yourself from all lightning alerts.</p>

      <div className="ls-warning-box">
        ⚠️ This will permanently delete your subscription and all associated locations.
      </div>

      <form onSubmit={handleSubmit}>
        <div className="ls-field">
          <label className="ls-label">Email</label>
          <input className="ls-input" type="email" placeholder="you@example.com" value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })} />
        </div>

        <div className="ls-field-last">
          <label className="ls-label">Phone Number</label>
          <input className="ls-input" type="tel" placeholder="+1 (555) 000-0000" value={form.phone_number}
            onChange={e => setForm({ ...form, phone_number: e.target.value })} />
        </div>

        {showTurnstile && (
          <div className="ls-turnstile">
            <Turnstile siteKey={TURNSTILE_SITE_KEY}
              options={{ theme: 'light', size: 'normal', language: 'auto' }}
              onSuccess={token => { setTurnstileToken(token); submit(token); }}
            />
          </div>
        )}

        {status === 'error' && <StatusBanner status="error" message={message} />}

        <button type="submit" className="ls-submit-btn danger" disabled={status === 'loading'}>
          {status === 'loading' ? 'Unsubscribing...' : 'Unsubscribe'}
        </button>
      </form>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const LightningSubscription = () => {
  const [view, setView] = useState<View>('menu');
  const [locations, setLocations] = useState<Location[]>([]);

  useEffect(() => {
    fetch(`/api/public/sms_locations`)
      .then(r => r.ok ? r.json() : [])
      .then(setLocations)
      .catch(() => setLocations([]));
  }, []);

  return (
    <div className="ls-page">

      <div className="ls-header">
        <img
          src={fnnLogo}
          alt="FNN Logo"
          className="fnn-header-logo ls-logo"
          onClick={() => window.open('https://fireneuralnetwork.com/', '_blank', 'noopener,noreferrer')}
        />
        <h1 className="ls-header-title">FNN™ Storm Alert System</h1>
      </div>

      <div className="ls-center">
        <div className="ls-panel">
          {view === 'menu' && <MenuView onSelect={setView} />}
          {view === 'subscribe' && <SubscribeView onBack={() => setView('menu')} locations={locations} />}
          {view === 'update' && <UpdateView onBack={() => setView('menu')} locations={locations} />}
          {view === 'unsubscribe' && <UnsubscribeView onBack={() => setView('menu')} />}
        </div>
      </div>

    </div>
  );
};

export default LightningSubscription;